/**
 * NS theme — palette + theme-niveau resets (shape, spacing, shadows,
 * typography). Per-component `overrides` (CP3a/b/c) komen later.
 *
 * Implementatie via `createUnifiedThemeFromV4` zodat MUI v4 `ThemeOptions` in
 * volle breedte beschikbaar zijn (shape, spacing, shadows, typography.button).
 * `createUnifiedTheme` accepteert deze keys niet (zie
 * `@backstage/theme/dist/unified/UnifiedTheme.esm.js` — `createBaseThemeOptions`
 * strijkt extra velden weg).
 *
 * Single source of truth:
 * - kleuren: `./colors.ts`
 * - typography: `./typography-tokens.ts`
 * - shadows: CSS custom properties uit `./tokens.css` (mode-aware via :root /
 *   `[data-theme-mode='dark']`).
 * - per-component overrides: `./theme-overrides.ts`
 */
import {
  createUnifiedThemeFromV4,
  type PageTheme,
  pageTheme as defaultPageThemes,
  UnifiedTheme,
} from '@backstage/theme';
import type { Shadows } from '@material-ui/core/styles/shadows';

import { MuiDrawerProps } from './components/drawer/mui-drawer-overrides';
import { TractieChevronDownIcon } from './components/select/tractie-chevron-icon';
import { colors } from './colors';
import { NS_SHADOW_ELEVATED_1, NS_SHADOW_ELEVATED_2 } from './shadow-tokens';
import { NS_DARK_OVERRIDES, NS_LIGHT_OVERRIDES } from './theme-overrides';
import {
  BUTTON_FONT_WEIGHT,
  FONT_FAMILY,
  H1_FONT_SIZE,
  H1_MARGIN_BOTTOM,
  H2_FONT_SIZE,
  H2_MARGIN_BOTTOM,
  H3_FONT_SIZE,
  H3_MARGIN_BOTTOM,
  H4_FONT_SIZE,
  H4_MARGIN_BOTTOM,
  H5_FONT_SIZE,
  H5_MARGIN_BOTTOM,
  H6_FONT_SIZE,
  H6_MARGIN_BOTTOM,
  HEADING_FONT_WEIGHT,
  HTML_FONT_SIZE,
} from './typography-tokens';

// MUI Select chevron: exacte Tractie-spec SVG-path (rounded v-stroke).
// MUI's default `ArrowDropDown` is filled triangle, `KeyboardArrowDown` is
// een andere angle. Voor pixel-pariteit: TractieChevronDownIcon (zelfde
// SVG-path als Tractie's eigen shadow-DOM chevron mask).
const MuiSelectProps = { IconComponent: TractieChevronDownIcon } as const;

type NsThemeOptions = Parameters<typeof createUnifiedThemeFromV4>[0] & {
  getPageTheme: (sel: { themeId: string }) => PageTheme;
  page: PageTheme;
};

const BURSTS_GRADIENT = 'var(--ns-bursts-gradient-linear)';

/**
 * MUI `shadows` is een tuple van precies 25 strings (`Shadows`-tuple).
 * Backstage zet bij dark normaal `--bui-shadow: none`, maar onze CSS-token
 * `--ns-shadow-elevated-*` resolved in dark naar `none` via tokens.css zelf.
 * Dus we hoeven hier geen mode-split te doen.
 */
const NS_SHADOWS: Shadows = [
  'none',
  NS_SHADOW_ELEVATED_1,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
  NS_SHADOW_ELEVATED_2,
];

const SHARED_SHAPE = { borderRadius: 6 };

/**
 * Backstage `<Page>` reads `theme.getPageTheme({ themeId })` from the MUI v4
 * theme to compute its header styling. `createUnifiedTheme` injects this via
 * `createBaseThemeOptions`; `createUnifiedThemeFromV4` does not. We add it
 * manually here, using the upstream default page theme map.
 *
 * Designer-driven NS page-theme colors (per-area branding) live in p9a6.
 */
const NS_DEFAULT_PAGE_THEME_ID = 'home';
const NS_PAGE_THEMES: Record<string, PageTheme> = defaultPageThemes;
const NS_DEFAULT_PAGE: PageTheme = (() => {
  const p = NS_PAGE_THEMES[NS_DEFAULT_PAGE_THEME_ID];
  if (!p) {
    throw new Error(
      `Default page theme '${NS_DEFAULT_PAGE_THEME_ID}' missing from defaultPageThemes`,
    );
  }
  return p;
})();
const NS_GET_PAGE_THEME = ({ themeId }: { themeId: string }): PageTheme =>
  NS_PAGE_THEMES[themeId] ?? NS_DEFAULT_PAGE;

/** MUI v4 `spacing` als getal: `theme.spacing(n)` levert n * 4 (number). */
const SHARED_SPACING = 4;

const SHARED_TYPOGRAPHY = {
  button: {
    // MUI's `button` typography variant defaults to 0.875rem (14px) / line-height
    // 1.75, and is spread onto the MUI Tab root and onto every
    // `Typography variant="button"` label (the framework wraps entity-tab labels
    // in one). Pin it to size-m (16px) + leading-l (1.5) so tabs and button text
    // follow the deliberate Tractie deviation from the 14px body, in one place,
    // and the tab row stays 48px instead of growing to 52px.
    fontSize: H5_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    letterSpacing: 'normal',
    lineHeight: 'var(--ns-font-leading-l)',
    textTransform: 'none' as const,
  },
  fontFamily: FONT_FAMILY,
  h1: {
    fontSize: H1_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H1_MARGIN_BOTTOM,
  },
  h2: {
    fontSize: H2_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H2_MARGIN_BOTTOM,
  },
  h3: {
    fontSize: H3_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H3_MARGIN_BOTTOM,
  },
  h4: {
    fontSize: H4_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H4_MARGIN_BOTTOM,
  },
  h5: {
    fontSize: H5_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H5_MARGIN_BOTTOM,
  },
  h6: {
    fontSize: H6_FONT_SIZE,
    fontWeight: HEADING_FONT_WEIGHT,
    marginBottom: H6_MARGIN_BOTTOM,
  },
  htmlFontSize: HTML_FONT_SIZE,
};

export const nsLightTheme: UnifiedTheme = createUnifiedThemeFromV4({
  // Backstage Page extensions (createUnifiedThemeFromV4 does not inject these).
  getPageTheme: NS_GET_PAGE_THEME,
  overrides: NS_LIGHT_OVERRIDES,
  page: NS_DEFAULT_PAGE,
  palette: {
    background: {
      default: colors.light.bgSunken,
      paper: colors.light.bgDefault,
    },

    banner: {
      error: colors.light.bgErrorDefault,
      info: colors.light.bgInfoDefault,
      link: colors.light.fgInteractiveDefault,
      text: colors.light.bgDefaultInverse,
      warning: colors.light.bgWarningDefault,
    },

    border: colors.light.borderInteractiveDefault,

    bursts: {
      backgroundColor: { default: colors.light.burstsBgDefault },
      fontColor: 'var(--ns-bursts-font-color)',
      gradient: { linear: BURSTS_GRADIENT },
      slackChannelText: 'var(--ns-bursts-slack-channel-text)',
    },

    code: { background: colors.light.codeBg },

    error: { main: colors.light.bgErrorDefault },

    errorBackground: colors.light.bgErrorDefault,

    errorText: colors.light.fgError,

    gold: colors.light.fgGold,

    highlight: colors.light.bgEmphasisPrimary,

    info: { main: colors.light.bgInfoDefault },

    infoBackground: colors.light.bgInfoDefault,

    infoText: colors.light.fgInfo,

    link: colors.light.fgInteractiveDefault,

    linkHover: colors.light.fgInteractiveHover,

    navigation: {
      background: colors.light.bgElevated1,
      color: colors.light.bgDefaultInverse,
      indicator: colors.light.navIndicator,
      // SidebarSubmenuItem hover: core-components valt zonder deze waarde terug
      // op een hardcoded #6f6f6f (donkergrijs). Tractie hover = transparante
      // overlay die op elke achtergrond stapelt.
      navItem: { hoverBackground: 'var(--ns-color-bg-hover)' },
      selectedColor: colors.light.fgInteractiveDefault,
      // SidebarSubmenu drawer-achtergrond. Zonder deze waarde valt
      // core-components terug op een hardcoded #404040 (donkergrijs in NS
      // Light). bg-elevated-2 (= het oude --ns-nav-submenu-bg token) houdt de
      // submenu coherent met de sidebar. Theme-waarde i.p.v. CSS-override zodat
      // het in prod werkt (de makeStyles-class is dev-only).
      submenu: { background: colors.light.bgElevated2 },
    },

    pinSidebarButton: {
      background: colors.light.bgEmphasisSecondary,
      icon: colors.light.bgDefaultInverse,
    },

    primary: {
      dark: colors.light.bgInteractivePressed,
      light: colors.light.bgInteractiveHover,
      main: colors.light.bgInteractiveDefault,
    },

    secondary: { main: colors.light.bgBrandSecondary },

    status: {
      aborted: colors.light.statusAborted,
      error: colors.light.bgErrorDefault,
      ok: colors.light.bgSuccessDefault,
      pending: colors.light.statusPending,
      running: colors.light.statusRunning,
      warning: colors.light.bgWarningDefault,
    },

    success: { main: colors.light.bgSuccessDefault },

    tabbar: { indicator: colors.light.navIndicator },

    textContrast: colors.light.bgDefaultInverse,

    textSubtle: colors.light.fgEmphasis,

    textVerySubtle: colors.light.bgEmphasisSecondary,

    type: 'light',

    warning: { main: colors.light.bgWarningDefault },

    warningBackground: colors.light.bgWarningDefault,

    warningText: colors.light.fgWarning,
  },
  props: {
    // ground-rules.md "Motion and text" (geen animatie): ripple uit op alle ButtonBase-descendants
    // (Button, IconButton, ListItem button, MenuItem, Tab, etc.).
    MuiButtonBase: { disableRipple: true },
    MuiDrawer: MuiDrawerProps,
    MuiSelect: MuiSelectProps,
  },
  shadows: NS_SHADOWS,
  shape: SHARED_SHAPE,
  spacing: SHARED_SPACING,
  typography: SHARED_TYPOGRAPHY,
} satisfies NsThemeOptions);

export const nsDarkTheme: UnifiedTheme = createUnifiedThemeFromV4({
  // Backstage Page extensions (createUnifiedThemeFromV4 does not inject these).
  getPageTheme: NS_GET_PAGE_THEME,
  overrides: NS_DARK_OVERRIDES,
  page: NS_DEFAULT_PAGE,
  palette: {
    background: {
      default: colors.dark.bgSunken,
      paper: colors.dark.bgDefault,
    },

    banner: {
      error: colors.dark.bgErrorDefault,
      info: colors.dark.bgInfoDefault,
      link: colors.dark.fgInteractiveDefault,
      text: colors.dark.bgDefaultInverse,
      warning: colors.dark.bgWarningDefault,
    },

    border: colors.dark.borderInteractiveDefault,

    bursts: {
      backgroundColor: { default: colors.dark.burstsBgDefault },
      fontColor: 'var(--ns-bursts-font-color)',
      gradient: { linear: BURSTS_GRADIENT },
      slackChannelText: 'var(--ns-bursts-slack-channel-text)',
    },

    code: { background: colors.dark.codeBg },

    error: { main: colors.dark.bgErrorDefault },

    errorBackground: colors.dark.bgErrorDefault,

    errorText: colors.dark.fgError,

    gold: colors.dark.fgGold,

    highlight: colors.dark.bgEmphasisPrimary,

    info: { main: colors.dark.bgInfoDefault },

    infoBackground: colors.dark.bgInfoDefault,

    infoText: colors.dark.fgInfo,

    link: colors.dark.fgInteractiveDefault,

    linkHover: colors.dark.fgInteractiveHover,

    navigation: {
      background: colors.dark.bgElevated1,
      color: colors.dark.bgDefaultInverse,
      indicator: colors.dark.navIndicator,
      // Zie light-thema: hover-overlay i.p.v. hardcoded #6f6f6f.
      navItem: { hoverBackground: 'var(--ns-color-bg-hover)' },
      selectedColor: colors.dark.fgInteractiveDefault,
      // Zie light-thema: SidebarSubmenu drawer-achtergrond als theme-waarde
      // (voorkomt de hardcoded #404040 fallback), prod-safe.
      submenu: { background: colors.dark.bgElevated2 },
    },

    pinSidebarButton: {
      background: colors.dark.bgEmphasisSecondary,
      icon: colors.dark.bgDefaultInverse,
    },

    primary: {
      dark: colors.dark.bgInteractivePressed,
      light: colors.dark.bgInteractiveHover,
      main: colors.dark.bgInteractiveDefault,
    },

    secondary: { main: colors.dark.bgBrandSecondary },

    status: {
      aborted: colors.dark.statusAborted,
      error: colors.dark.bgErrorDefault,
      ok: colors.dark.bgSuccessDefault,
      pending: colors.dark.statusPending,
      running: colors.dark.statusRunning,
      warning: colors.dark.bgWarningDefault,
    },

    success: { main: colors.dark.bgSuccessDefault },

    tabbar: { indicator: colors.dark.navIndicator },

    textContrast: colors.dark.bgDefaultInverse,

    textSubtle: colors.dark.fgEmphasis,

    textVerySubtle: colors.dark.bgEmphasisSecondary,

    type: 'dark',

    warning: { main: colors.dark.bgWarningDefault },

    warningBackground: colors.dark.bgWarningDefault,

    warningText: colors.dark.fgWarning,
  },
  props: {
    // ground-rules.md "Motion and text" (geen animatie): ripple uit op alle ButtonBase-descendants
    // (Button, IconButton, ListItem button, MenuItem, Tab, etc.).
    MuiButtonBase: { disableRipple: true },
    MuiDrawer: MuiDrawerProps,
    MuiSelect: MuiSelectProps,
  },
  shadows: NS_SHADOWS,
  shape: SHARED_SHAPE,
  spacing: SHARED_SPACING,
  typography: SHARED_TYPOGRAPHY,
} satisfies NsThemeOptions);
